import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// 动画上下文类型定义
interface AnimationContextType {
  isPageLoading: boolean; // 页面是否在加载中
  isPageChanging: boolean; // 页面是否在切换中
  setIsPageLoading: (isLoading: boolean) => void; // 设置页面加载状态
}

// 创建动画上下文
const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

// 上下文提供者属性
interface AnimationProviderProps {
  children: ReactNode;
}

// 动画上下文提供者组件
export const AnimationProvider: React.FC<AnimationProviderProps> = ({ children }) => {
  // 状态定义
  const [isPageLoading, setIsPageLoading] = useState(true); // 初始加载状态为true
  const [isPageChanging, setIsPageChanging] = useState(false);
  const location = useLocation();

  // 初始页面加载效果
  useEffect(() => {
    // 设置初始页面加载完成的延迟
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1500); // 1.5秒的页面加载动画

    return () => clearTimeout(timer);
  }, []);

  // 监听路由变化以触发页面切换动画
  useEffect(() => {
    // 路由变化时设置页面切换状态
    setIsPageChanging(true);
    
    // 页面切换完成后重置状态
    const timer = setTimeout(() => {
      setIsPageChanging(false);
    }, 500); // 0.5秒的页面切换动画

    return () => clearTimeout(timer);
  }, [location]);

  // 提供上下文值
  const value = {
    isPageLoading,
    isPageChanging,
    setIsPageLoading,
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};

// 自定义钩子，用于在组件中使用动画上下文
export const useAnimation = (): AnimationContextType => {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimation must be used within an AnimationProvider');
  }
  return context;
}; 